declare let gtag: Function;

import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DossierService } from '../../services/dossier.service';
import { AuthService, KeypairData } from '../../services/auth.service';
import { CryptoService } from '../../services/crypto.service';
import { ExtractionResult, Document, DocumentStatus, Page, Dossier, ModelTier, FileSignatureStatus, SignatureStatus } from '../../models/dossier.model';
import { environment } from '../../../environments/environment';
import {
  ViewMode,
  XmlTreeNode,
  CsvTable,
  getVisibleKeys,
  isArray,
  isObject,
  getObjectKeys,
  formatValue,
  parseXmlToTree,
  isXmlExtractionResult,
  isTableExtractionResult,
  isCsvExtractionResult,
  parseCsvTable,
  asValueConfidencePair,
  confidenceClass,
  confidencePercent,
} from '../../utils/extraction-render.utils';

/** MIME types for deterministic TABLE export formats. */
const TABLE_FORMAT_MIME: Record<string, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  odf: 'application/vnd.oasis.opendocument.spreadsheet',
};

/** A node in the editable extraction tree view. */
interface TreeNode {
  key: string;
  path: string;
  value: unknown;
  type: 'object' | 'array' | 'leaf';
  children: TreeNode[];
  expanded: boolean;
}

@Component({
  selector: 'app-extraction-review',
  templateUrl: './extraction-review.component.html',
  styleUrls: ['./extraction-review.component.css'],
})
export class ExtractionReviewComponent implements OnInit, OnDestroy {

  // --- Route params ---
  dossierId = '';
  uploadId = '';
  documentId = '';

  // --- Data ---
  extractionResult: ExtractionResult | null = null;
  treeNodes: TreeNode[] = [];
  pages: Page[] = [];
  pageImageUrls: Record<string, string> = {};
  documentType = '';
  documentNumber = 0;
  /** All documents in this upload — used for prev/next navigation. */
  uploadDocuments: Document[] = [];

  // --- OCR highlighting ---
  /** Cached OCR data per page oid. */
  pageOcrData: Record<string, { rec_texts: string[]; rec_scores: number[]; rec_boxes: number[][][] }> = {};
  /** Word currently being highlighted across page images. */
  highlightWord: string | null = null;
  /** Number of OCR boxes matched in the most recent draw — used by the no-match indicator. */
  highlightMatchCount = 0;

  // --- Rich rendering ---
  ViewMode = ViewMode;
  viewMode: ViewMode = ViewMode.TREE;
  parsedXmlTree: XmlTreeNode | null = null;
  csvTable: CsvTable | null = null;

  // --- Re-extract controls ---
  models: string[] = environment.modelOptions;
  selectedModel = '';
  selectedBackend: 'openai' | 'ollama' | 'bedrock' | 'local' = 'openai';
  internalDocumentTypes: string[] = [];
  externalDocumentTypes: string[] = [];
  schemaSource: 'internal' | 'external' = 'internal';
  selectedDocumentType = '';
  selectedOutputFormat = 'json';
  outputFormatsMap: Record<string, string[]> = { default: ['json', 'xml'] };
  reExtracting = false;

  // --- Edit mode ---
  /** When false (default), edits accumulate in draftResult and are only
   *  persisted on explicit Save. When true, each field change is saved
   *  immediately (legacy auto-save behaviour). Has no effect on XML
   *  documents — those always auto-save because the server must
   *  reconstruct the XML string. */
  autoSave = false;
  /** True when there are unsaved draft edits (JSON only). */
  isDirty = false;
  /** True when any field has been manually edited since the last extraction
   *  (covers both unsaved JSON drafts and auto-saved XML edits). Cleared
   *  only when fresh extraction data is loaded. Used for the re-extract warning. */
  hasManualEdits = false;
  private draftResult: Record<string, unknown> | null = null;

  // --- State ---
  loading = true;
  error = '';
  saving = false;
  markingReviewed = false;
  extractionFeedback: 'up' | 'down' | null = null;

  // --- DEK unlock state ---
  needsDekUnlock = false;
  dekUnlocked = false;
  dekUnlocking = false;
  dekPassword = '';
  dekError = '';
  noKeypairFound = false;
  private dossierForDek: Dossier | null = null;
  private keypairData: KeypairData | null = null;
  /** Action to retry after successful DEK unlock. */
  private pendingAction: (() => void) | null = null;

  // --- Paywall ---
  showPaywall = false;
  paywallMinBalance = 1000;
  paypalHostedButtonId = '';

  // --- Resizable images panel ---
  imagePanelWidth = 500;
  private resizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  private pollSub: Subscription | null = null;

  /** Model tiers for non-developer users */
  modelTiers: ModelTier[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    public auth: AuthService,
    private cryptoService: CryptoService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.dossierId = this.route.snapshot.paramMap.get('id') || '';
    this.uploadId = this.route.snapshot.paramMap.get('uid') || '';
    this.documentId = this.route.snapshot.paramMap.get('did') || '';

    if (this.dossierId && this.uploadId && this.documentId) {
      this.loadExtraction();
      this.loadModels();
      this.loadDocumentTypes();
      this.loadConfig();
      this.loadFileSignatures();
    }
  }

  /** Per-file signature badges for a batch/ZIP upload — a single PDF upload's
   *  badge is shown on the dossier page instead, since one upload is one
   *  document there. Silently shows nothing for a single-file upload (the
   *  endpoint returns the non-batch shape, which fileSignatures ignores). */
  fileSignatures: FileSignatureStatus[] = [];

  private loadFileSignatures(): void {
    this.dossierService.getUploadSignature(this.dossierId, this.uploadId).subscribe({
      next: (data) => {
        this.fileSignatures = 'files' in data ? data.files : [];
      },
      error: () => {
        this.fileSignatures = [];
      },
    });
  }

  signatureDialogTitle: string | null = null;
  signatureDialogResult: SignatureStatus | null = null;

  openSignatureDialog(filename: string | null, result: SignatureStatus): void {
    this.signatureDialogTitle = filename;
    this.signatureDialogResult = result;
  }

  closeSignatureDialog(): void {
    this.signatureDialogResult = null;
  }

  /** A re-check re-verifies every file in the batch, so refresh the whole
   *  badge row rather than just the one the panel was opened from. */
  onSignatureRechecked(result: SignatureStatus): void {
    this.signatureDialogResult = result;
    this.loadFileSignatures();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    Object.values(this.pageImageUrls).forEach(url => URL.revokeObjectURL(url));
  }

  // --- Data loading ---

  loadExtraction(): void {
    this.clearDraft();
    this.loading = true;
    this.error = '';
    this.dossierService.getExtraction(this.dossierId, this.uploadId, this.documentId).subscribe({
      next: (result) => {
        this.extractionResult = result;
        this.loading = false;
        // Set model selector from extraction result if available
        this.applyModelFromResult();
        // Always load document list for prev/next navigation
        this.loadPages();
        if (result.status === DocumentStatus.EXTRACTION_FAILED) {
          this.error = 'Extraction failed — use Re-extract to retry';
        } else if (result.status !== DocumentStatus.EXTRACTED && result.status !== DocumentStatus.REVIEWED) {
          this.startPolling();
        } else {
          this.buildTree();
          this.updateXmlTree();
          this.updateCsvTable();
        }
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 423) {
          this.showDekUnlock(() => this.loadExtraction());
        } else {
          this.error = err?.error?.detail || err?.message || 'Failed to load extraction';
        }
      },
    });
  }

  private loadModels(): void {
    // Model list comes from environment.modelOptions (includes pricing).
    // Server-side ALLOWED_MODELS is used only for validation, not UI population.
    this.autoSelectModel();
  }

  private loadDocumentTypes(): void {
    this.dossierService.getDocumentTypes().subscribe({
      next: (res) => {
        this.internalDocumentTypes = res.internal || [];
        this.externalDocumentTypes = res.external || [];
        if (res.output_formats) {
          this.outputFormatsMap = res.output_formats;
        }
        // Re-apply defaults if document was loaded before types arrived
        this.applyDocumentDefaults();
      },
    });
  }

  private loadConfig(): void {
    this.dossierService.getConfig().subscribe({
      next: (res: any) => {
        const defaults = res?.defaults;
        if (defaults) {
          if (defaults.extract_backend) this.selectedBackend = defaults.extract_backend;
          if (defaults.model) this.selectedModel = defaults.model;
        }
        const tiers = res?.model_tiers;
        if (Array.isArray(tiers) && tiers.length > 0) {
          this.modelTiers = tiers;
          if (!this.selectedModel) {
            const std = tiers.find((t: any) => t.key === 'standard');
            if (std) this.selectedModel = std.model;
          }
        }
        if (res?.paypal?.hosted_button_id) {
          this.paypalHostedButtonId = res.paypal.hosted_button_id;
        }
        this.autoSelectModel();
      },
    });
  }

  /** Ensure selectedModel is valid for the current backend filter. */
  private autoSelectModel(): void {
    const filtered = this.filteredModels;
    if (filtered.length && !filtered.includes(this.selectedModel)) {
      this.selectedModel = filtered[0];
    }
  }

  /** Set model selector from the extraction_model field stored in extraction_result. */
  private applyModelFromResult(): void {
    const er = this.extractionResult?.extraction_result;
    if (!er || typeof er !== 'object') return;
    const model = (er as any).extraction_model;
    if (!model || typeof model !== 'string') return;

    // Derive backend from model prefix
    if (model.startsWith('bedrock/')) {
      this.selectedBackend = 'bedrock';
    } else if (model.startsWith('ollama/')) {
      this.selectedBackend = 'ollama';
    } else if (model.startsWith('local/')) {
      this.selectedBackend = 'local';
    } else {
      this.selectedBackend = 'openai';
    }
    this.selectedModel = model;
  }

  /** Billing tokens from extraction result (same as simple flow). */
  get billingTokens(): number | null {
    const er = this.extractionResult?.extraction_result;
    if (!er || typeof er !== 'object') return null;
    const bt = (er as any).billing_tokens;
    if (!bt || typeof bt !== 'object') return null;
    return bt.extraction ?? null;
  }

  /** Model name from extraction result. */
  get extractionModel(): string | null {
    const er = this.extractionResult?.extraction_result;
    if (!er || typeof er !== 'object') return null;
    return (er as any).extraction_model ?? null;
  }

  /** Token usage from extraction result. */
  private get tokenUsage(): { prompt_tokens: number; completion_tokens: number } | null {
    const er = this.extractionResult?.extraction_result;
    if (!er || typeof er !== 'object') return null;
    const tu = (er as any).token_usage;
    if (!tu || typeof tu !== 'object') return null;
    return tu;
  }

  /** Estimated LLM cost (dev-only, same formula as simple flow). */
  get estimatedCost(): string | null {
    const usage = this.tokenUsage;
    if (!usage) return null;
    const model = this.extractionModel;
    if (!model) return null;
    const pricing = (environment.modelPricing as any)[model];
    if (!pricing) return null;
    const cost = (usage.prompt_tokens / 1_000_000) * pricing.input
               + (usage.completion_tokens / 1_000_000) * pricing.output;
    return cost < 0.001 ? '< $0.001' : `$${cost.toFixed(4)}`;
  }

  /** Whether the current user is a developer. */
  get isDeveloper(): boolean {
    return this.auth.isDeveloper;
  }

  // --- Polling ---

  startPolling(): void {
    this.pollSub = this.dossierService.pollExtractionStatus(
      this.dossierId, this.uploadId, this.documentId,
    ).pipe(
      takeWhile(result =>
        result.status !== DocumentStatus.EXTRACTED
        && result.status !== DocumentStatus.REVIEWED
        && result.status !== DocumentStatus.EXTRACTION_FAILED, true),
    ).subscribe({
      next: (result) => {
        this.extractionResult = result;
        if (result.status === DocumentStatus.EXTRACTED || result.status === DocumentStatus.REVIEWED) {
          this.stopPolling();
          this.reExtracting = false;
          this.clearDraft();
          this.buildTree();
          this.updateXmlTree();
          this.updateCsvTable();
          this.loadPages();
        }
        if (result.status === DocumentStatus.EXTRACTION_FAILED) {
          this.stopPolling();
          this.reExtracting = false;
          const errResult = result.extraction_result as Record<string, unknown> | null;
          this.error = (errResult?.['error_detail'] as string) || 'Extraction failed';
        }
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Polling failed';
        this.reExtracting = false;
      },
    });
  }

  stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  // --- Page images ---

  /** Raw document type from DB — stored so applyDocumentDefaults can run
   *  after both loadPages and loadDocumentTypes complete. */
  private pendingDocumentType = '';

  private loadPages(): void {
    this.dossierService.getDocuments(this.dossierId, this.uploadId).subscribe({
      next: (docs) => {
        this.uploadDocuments = docs;
        const idx = docs.findIndex(d => d.oid === this.documentId);
        if (idx >= 0) {
          const doc = docs[idx];
          this.documentType = doc.document_type;
          this.documentNumber = idx + 1;
          this.pendingDocumentType = doc.document_type || '';
          this.applyDocumentDefaults();
          if (doc.pages) {
            this.pages = doc.pages;
            for (const page of this.pages) {
              this.loadPageImage(page);
              this.loadPageOcr(page);
            }
          }
        }
      },
    });
  }

  /** Apply re-extract control defaults from the document type.
   *  Safe to call multiple times — only acts when both the document type
   *  and the type lists are available. */
  private applyDocumentDefaults(): void {
    if (!this.pendingDocumentType) return;

    const isXsd = this.pendingDocumentType.startsWith('xsd/');
    const bare = isXsd ? this.pendingDocumentType.substring(4) : this.pendingDocumentType;
    const types = isXsd ? this.externalDocumentTypes : this.internalDocumentTypes;

    // Wait until the type list is loaded and contains the type
    if (types.length === 0) return;

    this.schemaSource = isXsd ? 'external' : 'internal';
    this.selectedDocumentType = bare;
    if (isXsd) {
      this.selectedOutputFormat = 'xml';
    }
  }

  private loadPageImage(page: Page): void {
    if (this.pageImageUrls[page.oid]) return;
    this.dossierService.getPageImage(this.dossierId, this.uploadId, page.oid).subscribe({
      next: (blob) => { this.pageImageUrls[page.oid] = URL.createObjectURL(blob); },
    });
  }

  /** Load OCR data for a page (cached). */
  private loadPageOcr(page: Page): void {
    if (this.pageOcrData[page.oid]) return;
    this.dossierService.getPageOcr(this.dossierId, this.uploadId, page.oid).subscribe({
      next: (data) => { this.pageOcrData[page.oid] = data; },
    });
  }

  /** Minimum word length for OCR highlight to avoid noise. */
  private readonly MIN_HIGHLIGHT_WORD_LENGTH = 2;

  /** Normalize text for matching: lowercase, strip punctuation, collapse whitespace. */
  private normalizeForMatch(s: string): string {
    return s.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
  }

  /** Redraw highlight canvases for all pages. */
  drawAllHighlights(): void {
    let total = 0;
    for (const page of this.pages) {
      total += this.drawPageHighlight(page);
    }
    this.highlightMatchCount = total;
  }

  /** Draw highlight polygons on a page's canvas overlay. Returns boxes drawn. */
  private drawPageHighlight(page: Page): number {
    const canvas = document.getElementById('ocr-canvas-' + page.oid) as HTMLCanvasElement;
    const img = document.getElementById('page-img-' + page.oid) as HTMLImageElement;
    if (!canvas || !img) return 0;

    // Match canvas size to displayed image size
    const rect = img.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!this.highlightWord) return 0;

    const ocr = this.pageOcrData[page.oid];
    if (!ocr || !ocr.rec_texts || !ocr.rec_boxes) return 0;

    // Detect coordinate system: if any coordinate > 1, it's pixel coords
    const isPixelCoords = ocr.rec_boxes.some(
      (box: number[][]) => box.some((pt: number[]) => pt[0] > 1 || pt[1] > 1)
    );

    // Get natural image dimensions for pixel coord scaling
    const naturalW = img.naturalWidth || rect.width;
    const naturalH = img.naturalHeight || rect.height;
    const scaleX = isPixelCoords ? rect.width / naturalW : rect.width;
    const scaleY = isPixelCoords ? rect.height / naturalH : rect.height;

    // Find best matching OCR box sequence using Viterbi-style alignment
    const matchIndices = this.findBestOcrMatch(this.highlightWord, ocr.rec_texts);

    ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
    ctx.strokeStyle = 'rgba(255, 200, 0, 0.5)';
    ctx.lineWidth = 1;

    let drawn = 0;
    for (const idx of matchIndices) {
      const box = ocr.rec_boxes[idx];
      if (!box || box.length < 3) continue;

      ctx.beginPath();
      ctx.moveTo(box[0][0] * scaleX, box[0][1] * scaleY);
      for (let j = 1; j < box.length; j++) {
        ctx.lineTo(box[j][0] * scaleX, box[j][1] * scaleY);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      drawn++;
    }
    return drawn;
  }

  /**
   * Find all OCR boxes whose concatenated text matches the target value.
   *
   * Algorithm (two-pass):
   * 1. For each start position find the best-scoring sequence of length 1..maxLen.
   * 2. Collect all non-overlapping positions that score within 85 % of the
   *    global best, greedily (highest score first).  This ensures every
   *    occurrence of the same token in the document is highlighted, not just
   *    the first one.
   *
   * Similarity: 2×LCS / (|a|+|b|), biased toward covering the target.
   */
  private findBestOcrMatch(target: string, ocrTexts: string[]): number[] {
    if (!target || !ocrTexts || ocrTexts.length === 0) return [];

    const targetLower = this.normalizeForMatch(target);
    if (!targetLower) return [];
    const targetWords = targetLower.split(' ');
    const maxSeqLen = Math.min(targetWords.length + 2, 8);
    const minSimilarity = 0.5;

    // Pass 1 — best sequence per start position
    interface Candidate { start: number; len: number; score: number; }
    const candidates: Candidate[] = [];
    let bestScore = minSimilarity;

    for (let start = 0; start < ocrTexts.length; start++) {
      let concat = '';
      let best: Candidate = { start, len: 0, score: 0 };
      for (let len = 1; len <= maxSeqLen && start + len <= ocrTexts.length; len++) {
        const boxText = this.normalizeForMatch(ocrTexts[start + len - 1] || '');
        if (!boxText) continue;
        concat = concat ? concat + ' ' + boxText : boxText;
        const score = this.textSimilarity(targetLower, concat);
        if (score > best.score) { best = { start, len, score }; }
      }
      if (best.score >= minSimilarity) {
        candidates.push(best);
        if (best.score > bestScore) { bestScore = best.score; }
      }
    }

    // Pass 2 — collect all non-overlapping matches within 85 % of best score
    const threshold = Math.max(minSimilarity, bestScore * 0.85);
    candidates.sort((a, b) => b.score - a.score); // highest score first
    const used = new Set<number>();
    const result: number[] = [];

    for (const { start, len, score } of candidates) {
      if (score < threshold) { continue; }
      const indices: number[] = [];
      for (let k = start; k < start + len; k++) { indices.push(k); }
      if (indices.some(i => used.has(i))) { continue; } // overlaps already-selected match
      indices.forEach(i => { used.add(i); result.push(i); });
    }

    return result.sort((a, b) => a - b);
  }

  /** Compute similarity between two strings (0..1) using longest common subsequence ratio. */
  private textSimilarity(a: string, b: string): number {
    if (a === b) return 1;
    const shorter = a.length <= b.length ? a : b;
    const longer = a.length > b.length ? a : b;
    if (longer.length === 0) return 0;

    // Check containment first (fast path)
    if (longer.includes(shorter)) return shorter.length / longer.length;

    // LCS length
    const m = shorter.length;
    const n = longer.length;
    // Use single-row DP for memory efficiency
    const prev = new Array(n + 1).fill(0);
    const curr = new Array(n + 1).fill(0);
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (shorter[i - 1] === longer[j - 1]) {
          curr[j] = prev[j - 1] + 1;
        } else {
          curr[j] = Math.max(prev[j], curr[j - 1]);
        }
      }
      for (let j = 0; j <= n; j++) { prev[j] = curr[j]; curr[j] = 0; }
    }
    const lcsLen = prev[n];
    // Similarity = LCS / max length, biased toward covering the target
    return (2 * lcsLen) / (a.length + b.length);
  }

  // --- Tree building ---

  buildTree(): void {
    const data = this.extractionData;
    if (data && typeof data === 'object') {
      this.treeNodes = this.buildNodes(data, '');
    } else {
      this.treeNodes = [];
    }
  }

  private buildNodes(obj: Record<string, unknown>, parentPath: string): TreeNode[] {
    const nodes: TreeNode[] = [];
    const entries = Array.isArray(obj)
      ? obj.map((v, i) => [String(i), v] as [string, unknown])
      : Object.entries(obj);

    for (const [key, value] of entries) {
      const path = parentPath ? `${parentPath}.${key}` : key;
      if (value !== null && typeof value === 'object') {
        const isArr = Array.isArray(value);
        nodes.push({
          key, path, value,
          type: isArr ? 'array' : 'object',
          children: this.buildNodes(value as Record<string, unknown>, path),
          expanded: true,
        });
      } else {
        nodes.push({ key, path, value, type: 'leaf', children: [], expanded: false });
      }
    }
    return nodes;
  }

  private updateXmlTree(): void {
    const result = this.extractionData;
    if (this.isXmlResult && result && (result as any).xml) {
      this.parsedXmlTree = parseXmlToTree((result as any).xml);
    } else {
      this.parsedXmlTree = null;
    }
  }

  private updateCsvTable(): void {
    const result = this.extractionData;
    if (this.isCsvResult && result && (result as any).csv) {
      this.csvTable = parseCsvTable((result as any).csv);
      this.viewMode = ViewMode.SPREADSHEET;
    } else {
      this.csvTable = null;
    }
  }

  // --- Rich rendering helpers (delegating to shared utils) ---

  get extractionData(): Record<string, unknown> | null {
    const raw = this.extractionResult?.extraction_result;
    // Native SWIFT MT / ISO 20022 documents have no extraction_result. Their
    // parsed fields are already canonical key/value pairs, so they render
    // through the same tree/table view rather than the "no result" message.
    if (!raw) {
      const fields = this.extractionResult?.native_message?.parsed_fields;
      return fields && Object.keys(fields).length ? { ...fields } : null;
    }
    // If the result was stored as a JSON string, parse it
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
  }

  /** True when this document is a native SWIFT MT / ISO 20022 message. */
  get isNativeMessage(): boolean {
    return !!this.extractionResult?.native_message;
  }

  /** Raw message text, decrypted server-side from the stored original. */
  get nativeRaw(): string {
    return this.extractionResult?.native_message?.raw ?? '';
  }

  /** Data used for display — local draft if dirty, otherwise server data. */
  get effectiveData(): Record<string, unknown> | null {
    return this.draftResult ?? this.extractionData;
  }

  private clearDraft(): void {
    this.draftResult = null;
    this.isDirty = false;
    this.hasManualEdits = false;
  }

  /** Deep-set a value at a dot-separated path (numeric segments treated as array indices). */
  private setValueAtPath(obj: any, path: string, value: unknown): void {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (current == null) return;
      current = current[parts[i]];
    }
    if (current != null) {
      current[parts[parts.length - 1]] = value;
    }
  }

  get isXmlResult(): boolean {
    return isXmlExtractionResult(this.extractionData);
  }

  get isTableResult(): boolean {
    return isTableExtractionResult(this.extractionData);
  }

  get isCsvResult(): boolean {
    return isCsvExtractionResult(this.extractionData);
  }

  /** Safe accessor for CSV string from extraction result. */
  get csvContent(): string | null {
    const d = this.extractionData as any;
    return d?.csv || null;
  }

  /** Safe accessor for XML string from extraction result. */
  get xmlContent(): string | null {
    const d = this.extractionData as any;
    return d?.xml || null;
  }

  /** Safe accessor for XSD validation status. */
  get xmlValid(): boolean | null {
    const d = this.extractionData as any;
    return d?.valid ?? null;
  }

  /** Safe accessor for XSD validation errors. */
  get xmlErrors(): string {
    const d = this.extractionData as any;
    return d?.errors || '';
  }

  /** Safe accessor for table format string. */
  get tableFormat(): string {
    const d = this.extractionData as any;
    return d?.format || '';
  }

  /** Safe accessor for table content. */
  get tableContent(): string | null {
    const d = this.extractionData as any;
    return d?.content || null;
  }

  /** True when the table result is a binary format (e.g. xlsx, base64-encoded)
   *  that cannot be displayed or copied as text — only downloaded. */
  get isBinaryTableResult(): boolean {
    const d = this.extractionData as any;
    return this.isTableResult && d?.encoding === 'base64';
  }

  /** Label for the download button. */
  get downloadLabel(): string {
    if (this.isCsvResult) return 'CSV';
    if (this.isTableResult) return this.tableFormat.toUpperCase();
    if (this.isXmlResult) return 'XML';
    return 'JSON';
  }

  getVisibleKeys(obj: any): string[] { return getVisibleKeys(obj); }
  isArray(value: any): boolean { return isArray(value); }
  isObject(value: any): boolean { return isObject(value); }
  getObjectKeys(obj: any): string[] { return getObjectKeys(obj); }
  formatValue(value: any): string { return formatValue(value); }
  asValueConfidencePair(obj: any) { return asValueConfidencePair(obj); }
  confidenceClass(c: number | null): string { return confidenceClass(c); }
  confidencePercent(c: number | null): string { return confidencePercent(c); }

  /** Split a display value into words for individual hover highlighting. */
  splitValueWords(value: string): string[] {
    if (!value) return [];
    return value.split(/\s+/).filter(w => w.length > 0);
  }

  /** Called on mouseenter of a whole value span — find best OCR boxes via Viterbi. */
  onValueHover(value: string): void {
    const clean = (value || '').replace(/^"|"$/g, '');
    if (this.normalizeForMatch(clean).length < this.MIN_HIGHLIGHT_WORD_LENGTH) {
      this.highlightWord = null;
      this.highlightMatchCount = 0;
      this.drawAllHighlights();
      return;
    }
    this.highlightWord = clean;
    this.drawAllHighlights();
  }

  /** Called on mouseleave of a value span. */
  onValueHoverOut(): void {
    this.highlightWord = null;
    this.drawAllHighlights();
  }

  /** Detect value+confidence pattern in XML tree nodes. */
  xmlNodeAsValueConfidence(node: any): { value: string; confidence: number | null } | null {
    if (!node || !node.children || node.children.length < 1 || node.children.length > 2) return null;
    const childTags = node.children.map((c: any) => c.tag);
    if (!childTags.includes('value')) return null;
    const valueNode = node.children.find((c: any) => c.tag === 'value');
    const confNode = node.children.find((c: any) => c.tag === 'confidence');
    // Both must be leaf nodes (no children of their own)
    if (valueNode.children.length > 0) return null;
    if (confNode && confNode.children.length > 0) return null;
    // If 2 children, second must be confidence
    if (node.children.length === 2 && !confNode) return null;
    const conf = confNode ? parseFloat(confNode.text) : null;
    return {
      value: valueNode.text || '',
      confidence: (conf !== null && !isNaN(conf)) ? conf : null,
    };
  }

  toggleViewMode(): void {
    if (this.isCsvResult) {
      this.viewMode = this.viewMode === ViewMode.SPREADSHEET ? ViewMode.JSON : ViewMode.SPREADSHEET;
    } else {
      this.viewMode = this.viewMode === ViewMode.TREE ? ViewMode.JSON : ViewMode.TREE;
    }
  }

  private checkPaywall(): boolean {
    const balance = this.auth.user?.balance ?? 0;
    if (balance < this.paywallMinBalance) {
      // Show the paywall modal instead of opening PayPal directly. The modal
      // branches on auth state: authenticated users see the PayPal top-up,
      // anonymous users are prompted to register/login (they cannot pay).
      this.showPaywall = true;
      return false;
    }
    return true;
  }

  openPaypalTopup(): void {
    const url = 'https://www.paypal.com/cgi-bin/webscr'
      + '?cmd=_s-xclick'
      + '&hosted_button_id=' + encodeURIComponent(this.paypalHostedButtonId)
      + '&currency_code=EUR'
      + '&custom=' + encodeURIComponent(this.auth.user?.email || '');
    window.open(url, '_blank');
  }

  copyResult(): void {
    if (!this.checkPaywall()) return;
    const result = this.extractionData;
    if (!result) return;
    if (this.isCsvResult && (result as any).csv) {
      navigator.clipboard.writeText((result as any).csv);
    } else if (this.isBinaryTableResult) {
      // Binary formats (xlsx) can't be copied as text — download instead.
      this.downloadResult();
    } else if (this.isTableResult && (result as any).content) {
      navigator.clipboard.writeText((result as any).content);
    } else if (this.isXmlResult && (result as any).xml) {
      navigator.clipboard.writeText((result as any).xml);
    } else {
      navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    }
  }

  downloadResult(): void {
    if (!this.checkPaywall()) return;
    const result = this.extractionData;
    if (!result) return;
    const filename = `document_${this.documentId}`;

    if (this.isCsvResult && (result as any).csv) {
      this.downloadBlob((result as any).csv, `${filename}_extraction.csv`, 'text/csv');
    } else if (this.isTableResult && (result as any).content) {
      const fmt = (result as any).format;
      const mime = TABLE_FORMAT_MIME[fmt] || 'application/octet-stream';
      if ((result as any).encoding === 'base64') {
        // Binary formats (xlsx) are delivered base64-encoded; decode to bytes.
        this.downloadBase64Blob((result as any).content, `${filename}_table.${fmt}`, mime);
      } else {
        this.downloadBlob((result as any).content, `${filename}_table.${fmt}`, mime);
      }
    } else if (this.isXmlResult && (result as any).xml) {
      this.downloadBlob((result as any).xml, `${filename}_extraction.xml`, 'application/xml');
    } else {
      this.downloadBlob(JSON.stringify(result, null, 2), `${filename}_extraction.json`, 'application/json');
    }
    if (typeof gtag === 'function') {
      gtag('event', 'download_result');
    }
  }

  /** Download a base64-encoded binary payload (e.g. xlsx) as a file. */
  private downloadBase64Blob(base64: string, filename: string, mimeType: string): void {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    this.downloadBlob(bytes, filename, mimeType);
  }

  private downloadBlob(content: string | Uint8Array, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Download the OCR text for all pages as a .txt, reflowed into lines. Paywall-gated. */
  downloadOcrText(): void {
    if (!this.checkPaywall()) return;
    if (!this.pages || this.pages.length === 0) return;

    // Ensure every page's OCR is loaded (it's fetched lazily on render).
    const requests = this.pages.map(p =>
      this.pageOcrData[p.oid]
        ? of(this.pageOcrData[p.oid])
        : this.dossierService.getPageOcr(this.dossierId, this.uploadId, p.oid)
    );
    forkJoin(requests).subscribe({
      next: (datas) => {
        const parts: string[] = [];
        this.pages.forEach((page, i) => {
          const data = datas[i];
          if (data) this.pageOcrData[page.oid] = data;
          parts.push(`--- Page ${page.page_number} ---`);
          parts.push(this.reconstructPageText(data));
          parts.push('');
        });
        this.downloadBlob(parts.join('\n'), `document_${this.documentId}_ocr.txt`, 'text/plain;charset=utf-8');
        if (typeof gtag === 'function') {
          gtag('event', 'dd_ocr_download', { format: 'txt', mode: 'dossier' });
        }
      },
    });
  }

  /** Reflow OCR tokens into lines using their bounding boxes (mirrors backend reconstruct_text). */
  private reconstructPageText(ocr: any): string {
    const texts: string[] = ocr?.rec_texts || [];
    const boxes: number[][][] = ocr?.rec_boxes || [];
    if (texts.length === 0) return '';
    if (boxes.length !== texts.length) return texts.join('\n');

    const tokens = texts.map((text, i) => {
      const box = boxes[i] || [];
      const ys = box.map(pt => pt[1]);
      const xs = box.map(pt => pt[0]);
      const yc = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 0;
      const h = ys.length ? Math.max(...ys) - Math.min(...ys) : 0;
      const x = xs.length ? Math.min(...xs) : 0;
      return { text, yc, x, h };
    });

    tokens.sort((a, b) => a.yc - b.yc || a.x - b.x);

    const lines: { text: string; x: number }[][] = [];
    let current = [tokens[0]];
    for (const tok of tokens.slice(1)) {
      const ref = current[current.length - 1];
      const tol = Math.max(ref.h, tok.h, 1) * 0.6;
      if (Math.abs(tok.yc - ref.yc) <= tol) {
        current.push(tok);
      } else {
        lines.push(current);
        current = [tok];
      }
    }
    lines.push(current);

    return lines
      .map(line => line.sort((a, b) => a.x - b.x).map(t => t.text).join(' '))
      .join('\n');
  }

  showValidationReport(errors: string): void {
    const w = window.open('', 'validation_report', 'width=720,height=500,scrollbars=yes,resizable=yes');
    if (!w) return;
    const escaped = errors.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    w.document.write(
      '<!DOCTYPE html><html><head><title>XSD Validation Report</title>' +
      '<style>body{font-family:monospace;font-size:13px;padding:16px;background:#f9f9f9;color:#333;}' +
      'pre{white-space:pre-wrap;word-break:break-word;background:#fff;border:1px solid #ddd;padding:12px;border-radius:4px;}</style>' +
      '</head><body><h2>XSD Validation Report</h2><pre>' + escaped + '</pre></body></html>',
    );
    w.document.close();
  }

  // --- Re-extract controls ---

  get activeDocumentTypes(): string[] {
    return this.schemaSource === 'external'
      ? this.externalDocumentTypes
      : this.internalDocumentTypes;
  }

  /** Models filtered by the selected backend provider. */
  get filteredModels(): string[] {
    switch (this.selectedBackend) {
      case 'ollama':
        return this.models.filter(m => m.startsWith('ollama/'));
      case 'bedrock':
        return this.models.filter(m => m.startsWith('bedrock/'));
      case 'local':
        return this.models.filter(m => m.startsWith('local/'));
      case 'openai':
      default:
        return this.models.filter(m => !m.startsWith('ollama/') && !m.startsWith('bedrock/') && !m.startsWith('local/'));
    }
  }

  /** Strip provider prefix from model name for display. */
  displayModel(m: string): string {
    const slash = m.indexOf('/');
    return slash >= 0 ? m.substring(slash + 1) : m;
  }

  /** Available output formats for the selected document type. */
  get activeOutputFormats(): string[] {
    return this.outputFormatsMap[this.selectedDocumentType]
      || this.outputFormatsMap['default']
      || ['json', 'xml'];
  }

  /** When backend changes, reset model to first available. */
  onBackendChange(): void {
    const filtered = this.filteredModels;
    if (filtered.length && !filtered.includes(this.selectedModel)) {
      this.selectedModel = filtered[0];
    }
  }

  onTierModelChange(model: string): void {
    this.selectedModel = model;
  }

  bareDocType(docType: string): string {
    return docType?.startsWith('xsd/') ? docType.substring(4) : docType;
  }

  get effectiveDocumentType(): string {
    return this.schemaSource === 'external'
      ? 'xsd/' + this.selectedDocumentType
      : this.selectedDocumentType;
  }

  onSchemaSourceChange(source: 'internal' | 'external'): void {
    this.schemaSource = source;
    const types = this.activeDocumentTypes;
    if (types.length && !types.includes(this.selectedDocumentType)) {
      this.selectedDocumentType = types[0];
    }
    // External (XSD) always produces XML
    if (source === 'external') {
      this.selectedOutputFormat = 'xml';
    }
    this.resetOutputFormatIfNeeded();
  }

  onDocumentTypeChange(): void {
    this.resetOutputFormatIfNeeded();
  }

  private resetOutputFormatIfNeeded(): void {
    // External schema always forces XML
    if (this.schemaSource === 'external') {
      this.selectedOutputFormat = 'xml';
      return;
    }
    const formats = this.activeOutputFormats;
    if (!formats.includes(this.selectedOutputFormat)) {
      this.selectedOutputFormat = formats[0] || 'json';
    }
  }

  reExtract(): void {
    if (this.reExtracting) return;
    if (this.isDirty || this.hasManualEdits) {
      const msg = this.isDirty
        ? 'You have unsaved manual edits. Re-extracting will discard them. Continue?'
        : 'You have manually edited fields. Re-extracting will replace them. Continue?';
      if (!window.confirm(msg)) {
        return;
      }
      this.clearDraft();
    }
    const model = this.selectedModel;
    const docType = this.effectiveDocumentType;
    if (!model || !docType) {
      this.error = 'Please select a model and document type';
      return;
    }

    this.reExtracting = true;
    this.error = '';
    this.dossierService.reExtract(
      this.dossierId, this.uploadId, this.documentId, model, docType, this.selectedOutputFormat,
    ).subscribe({
      next: () => {
        // Update local document type
        this.documentType = docType;
        // Start polling for the new result
        this.startPolling();
      },
      error: (err) => {
        this.reExtracting = false;
        if (err?.status === 423) {
          this.showDekUnlock(() => this.reExtract());
        } else {
          this.error = err?.error?.detail || err?.message || 'Re-extraction failed';
        }
      },
    });
  }

  // --- Field editing ---

  /** Path of the field currently being edited (null = none). */
  editingPath: string | null = null;
  /** Current value in the edit input. */
  editingValue = '';

  /** Start inline editing for a field. */
  startEdit(path: string, currentValue: unknown): void {
    this.editingPath = path;
    this.editingValue = currentValue === null || currentValue === undefined ? '' : String(currentValue);
  }

  /** Commit the edit on blur or Enter. */
  commitEdit(path: string, originalValue: unknown): void {
    if (this.editingPath !== path) return;
    const raw = this.editingValue;
    this.editingPath = null;
    const coerced = this.coerceValue(originalValue, raw);
    if (coerced === originalValue) return;

    // XML documents always auto-save — the server must reconstruct the XML string.
    // For JSON documents, respect autoSave flag.
    if (!this.autoSave && !this.isXmlResult) {
      if (!this.draftResult) {
        this.draftResult = JSON.parse(JSON.stringify(this.extractionData ?? {}));
      }
      this.setValueAtPath(this.draftResult, path, coerced);
      this.isDirty = true;
      this.hasManualEdits = true;
      return;
    }

    // XML field paths must be prefixed with 'xml.' for the server to route
    // them into the XML update logic rather than the JSON field path logic.
    const serverPath = this.isXmlResult ? 'xml.' + path : path;

    this.saving = true;
    this.hasManualEdits = true;
    this.dossierService.updateExtractionField(
      this.dossierId, this.uploadId, this.documentId, serverPath, coerced,
    ).subscribe({
      next: (result) => {
        this.extractionResult = result;
        this.buildTree();
        this.updateXmlTree();
        this.updateCsvTable();
        this.saving = false;
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to save field';
        this.saving = false;
      },
    });
  }

  /** Persist all pending draft edits to the server (JSON documents only). */
  saveAll(): void {
    if (!this.isDirty || !this.draftResult) return;
    this.saving = true;
    this.error = '';
    this.dossierService.saveExtraction(
      this.dossierId, this.uploadId, this.documentId, this.draftResult,
    ).subscribe({
      next: (result) => {
        this.extractionResult = result;
        this.clearDraft();
        this.buildTree();
        this.updateXmlTree();
        this.updateCsvTable();
        this.saving = false;
        if (this.isXmlResult) {
          this.runXsdValidation();
        }
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to save';
        this.saving = false;
      },
    });
  }

  /** After saving an XSD document, run server-side XSD validation and patch
   *  the in-memory extraction result with the new valid/errors values. */
  private runXsdValidation(): void {
    const data = this.extractionData;
    if (!data) return;
    this.dossierService.validateExtraction(
      this.dossierId, this.uploadId, this.documentId, data,
    ).subscribe({
      next: (res) => {
        const er = this.extractionResult?.extraction_result as any;
        if (er && typeof er === 'object') {
          er.valid = res.valid;
          er.errors = res.errors ?? null;
        }
      },
      error: (err) => {
        console.warn('XSD validation after save failed:', err);
      },
    });
  }

  /** Cancel editing on Escape. */
  cancelEdit(): void {
    this.editingPath = null;
  }

  /** Handle keydown in edit input. */
  onEditKeydown(event: KeyboardEvent, path: string, originalValue: unknown): void {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  // Legacy TreeNode-based editing (kept for compatibility)

  onFieldBlur(node: TreeNode, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.saveField(node, input.value);
  }

  onFieldKeydown(node: TreeNode, event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      this.saveField(node, input.value);
      input.blur();
    }
  }

  private saveField(node: TreeNode, rawValue: string): void {
    const coerced = this.coerceValue(node.value, rawValue);
    if (coerced === node.value) return;
    this.saving = true;
    this.dossierService.updateExtractionField(
      this.dossierId, this.uploadId, this.documentId, node.path, coerced,
    ).subscribe({
      next: (result) => {
        node.value = coerced;
        this.extractionResult = result;
        this.saving = false;
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to save field';
        this.saving = false;
      },
    });
  }

  private coerceValue(original: unknown, raw: string): unknown {
    if (typeof original === 'number') {
      const n = Number(raw);
      return isNaN(n) ? raw : n;
    }
    if (typeof original === 'boolean') return raw.toLowerCase() === 'true';
    if (original === null && raw === '') return null;
    return raw;
  }

  // --- Toggle tree nodes ---

  toggleNode(node: TreeNode): void {
    node.expanded = !node.expanded;
  }

  // --- Mark reviewed ---

  markReviewed(): void {
    this.markingReviewed = true;
    this.error = '';
    this.dossierService.markReviewed(this.dossierId, this.uploadId, this.documentId).subscribe({
      next: () => {
        this.markingReviewed = false;
        if (this.hasNextDocument) {
          this.goToNextDocument();
        } else {
          this.router.navigate(['/dossiers', this.dossierId, 'uploads', this.uploadId, 'review']);
        }
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to mark as reviewed';
        this.markingReviewed = false;
      },
    });
  }

  sendExtractionFeedback(rating: 'up' | 'down'): void {
    this.extractionFeedback = rating;
    if (typeof gtag === 'function') {
      gtag('event', 'extraction_feedback', {
        rating,
        document_type: this.selectedDocumentType,
      });
    }
  }

  // --- Resizable images panel ---

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.imagePanelWidth;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.resizing) return;
      // Dragging left increases panel width (panel is on the right)
      const delta = this.resizeStartX - e.clientX;
      this.imagePanelWidth = Math.max(250, Math.min(900, this.resizeStartWidth + delta));
    };

    const onMouseUp = () => {
      this.resizing = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // --- Navigation ---

  goBack(): void {
    this.router.navigate(['/dossiers', this.dossierId, 'uploads', this.uploadId, 'review']);
  }

  get totalDocuments(): number {
    return this.uploadDocuments.length;
  }

  get hasPrevDocument(): boolean {
    return this.documentNumber > 1;
  }

  get hasNextDocument(): boolean {
    return this.documentNumber > 0 && this.documentNumber < this.totalDocuments;
  }

  goToPrevDocument(): void {
    const idx = this.documentNumber - 2; // documentNumber is 1-based
    if (idx >= 0 && idx < this.uploadDocuments.length) {
      this.navigateToDocument(this.uploadDocuments[idx].oid);
    }
  }

  goToNextDocument(): void {
    const idx = this.documentNumber; // documentNumber is 1-based, so this is next
    if (idx >= 0 && idx < this.uploadDocuments.length) {
      this.navigateToDocument(this.uploadDocuments[idx].oid);
    }
  }

  private navigateToDocument(docOid: string): void {
    this.router.navigate([
      '/dossiers', this.dossierId,
      'uploads', this.uploadId,
      'documents', docOid,
      'extract',
    ]).then(() => {
      // Reset state and reload for the new document
      this.clearDraft();
      this.documentId = docOid;
      this.extractionResult = null;
      this.pages = [];
      this.treeNodes = [];
      this.parsedXmlTree = null;
      this.error = '';
      this.selectedDocumentType = '';
      this.pendingDocumentType = '';
      this.stopPolling();
      Object.values(this.pageImageUrls).forEach(url => URL.revokeObjectURL(url));
      this.pageImageUrls = {};
      this.pageOcrData = {};
      this.highlightWord = null;
      this.loadExtraction();
    });
  }

  // --- Helpers ---

  get isExtractionFailed(): boolean {
    return this.extractionResult?.status === DocumentStatus.EXTRACTION_FAILED;
  }

  get isExtracted(): boolean {
    return this.extractionResult?.status === DocumentStatus.EXTRACTED
      || this.extractionResult?.status === DocumentStatus.REVIEWED;
  }

  get isWaiting(): boolean {
    return !this.isExtracted && !this.error && !this.reExtracting;
  }

  get hasNoData(): boolean {
    const result = this.extractionData;
    return !result || (typeof result === 'object' && Object.keys(result).length === 0);
  }

  displayValue(value: unknown): string {
    if (value === null) return '';
    return String(value);
  }

  // --- DEK unlock ---

  /**
   * Load the dossier (to obtain encrypted_dek / key_slot) and show the DEK
   * unlock dialog. The supplied action is stored and retried after a
   * successful unlock.
   */
  private showDekUnlock(retryAction: () => void): void {
    this.pendingAction = retryAction;
    if (this.dossierForDek) {
      this.needsDekUnlock = true;
      this.dekUnlocked = false;
      return;
    }
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (dossier) => {
        this.dossierForDek = dossier;
        // If already unlocked in this session, just retry
        if (this.cryptoService.isDossierUnlocked(dossier.oid)) {
          this.pendingAction?.();
          this.pendingAction = null;
          return;
        }
        if (this.cryptoService.hasCachedPrivateKey()) {
          this.autoUnlockDek();
          return;
        }
        this.needsDekUnlock = true;
        this.dekUnlocked = false;
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to load dossier for unlock';
      },
    });
  }

  /**
   * Perform the full client-side DEK unlock using the entered password.
   * After success, retries the pending action.
   */
  async unlockDek(): Promise<void> {
    if (!this.dossierForDek?.encrypted_dek || !this.dekPassword) {
      this.dekError = this.translate.instant('dossierDetail.dekPasswordRequired');
      return;
    }

    this.dekUnlocking = true;
    this.dekError = '';

    try {
      let privateKey: CryptoKey;

      const cached = this.cryptoService.getCachedPrivateKey();
      if (cached) {
        privateKey = cached;
      } else {
        if (!this.keypairData) {
          this.keypairData = await this.auth.getKeypairData().toPromise() ?? null;
        }
        if (!this.keypairData) {
          this.dekError = this.translate.instant('dossierDetail.dekNoKeypair');
          this.noKeypairFound = true;
          this.dekUnlocking = false;
          return;
        }

        const slot = (this.dossierForDek.key_slot === 'B')
          ? this.keypairData.slot_b
          : this.keypairData.slot_a;
        if (!slot) {
          this.dekError = this.translate.instant('dossierDetail.dekNoKeypair');
          this.noKeypairFound = true;
          this.dekUnlocking = false;
          return;
        }

        const derivedKey = await this.cryptoService.deriveKeyFromPassword(
          this.dekPassword,
          slot.private_key_salt,
        );

        try {
          privateKey = await this.cryptoService.decryptPrivateKey(
            derivedKey,
            slot.encrypted_private_key,
            slot.private_key_nonce,
          );
        } catch {
          this.dekError = this.translate.instant('dossierDetail.dekWrongPassword');
          this.dekUnlocking = false;
          return;
        }
      }

      const dekHex = await this.cryptoService.unwrapDek(
        privateKey,
        this.dossierForDek.encrypted_dek,
      );

      await this.auth.unlockDek(this.dossierForDek.oid, dekHex).toPromise();

      this.dekUnlocked = true;
      this.needsDekUnlock = false;
      this.dekPassword = '';
      this.dekError = '';
      this.cryptoService.addUnlockedDossier(this.dossierForDek.oid);

      const action = this.pendingAction;
      this.pendingAction = null;
      action?.();
    } catch (err: any) {
      this.dekError = err?.error?.detail || err?.message
        || this.translate.instant('dossierDetail.dekUnlockFailed');
    } finally {
      this.dekUnlocking = false;
    }
  }

  private async autoUnlockDek(): Promise<void> {
    if (!this.dossierForDek?.encrypted_dek) { return; }
    const privateKey = this.cryptoService.getCachedPrivateKey();
    if (!privateKey) { return; }

    this.dekUnlocking = true;

    try {
      const dekHex = await this.cryptoService.unwrapDek(
        privateKey,
        this.dossierForDek.encrypted_dek,
      );
      await this.auth.unlockDek(this.dossierForDek.oid, dekHex).toPromise();

      this.cryptoService.addUnlockedDossier(this.dossierForDek.oid);
      const action = this.pendingAction;
      this.pendingAction = null;
      action?.();
    } catch {
      // Cached key failed — fall through to manual password prompt
      this.needsDekUnlock = true;
      this.dekUnlocked = false;
    } finally {
      this.dekUnlocking = false;
    }
  }

  openMyAccountSecurity(): void {
    (window as any).ddShowMyAccount?.('sec');
  }
}
